import {
    AppEnum,
    AppEnumHelper,
    DateFormatHelper,
    MandateEnums,
    OptionHelper,
    OTHER_TYPE,
    SALE_OPERATION_TYPE,
    RENT_MNGT_MANDATE_TYPE,
    SALE_MANDATE_TYPE,
    SEARCH_PROPERTY_RENT_MANADTE_TYPE,
    RecordMap,
    SoNumber,
    SoVAT,
} from "@/Shared";

import {
    UserModel,
    ContactModel,
    PropertyModel,
    NeedModel
} from '@/Domains';

import { MandateModel } from '../Models/MandateModel';
import { MandateFormValues } from "../MandateFormValidation";
import { json } from "stream/consumers";

export interface MandateFormVMParams {
    viewEnums: MandateEnums;
    contactMap: RecordMap<ContactModel>;
    userMap: RecordMap<UserModel>;
    propertyMap: RecordMap<PropertyModel>;
    needMap: RecordMap<NeedModel>;
    enrichedMandate?: MandateModel;
    property? : PropertyModel;
    need? : NeedModel;
}


/**
 * ViewModel: MandateFormViewModel
 *
 * Description:
 * ViewModel responsible for preparing and exposing all data required by the Mandate form.
 * It acts as a bridge between domain models and UI components by:
 * - Formatting initial form values
 * - Building selectable options for dropdown fields
 * - Computing display-oriented data such as the form title
 *
 * This ViewModel supports both creation and edition modes depending on the presence
 * of an enriched mandate.
 *
 * Responsibilities:
 * - Transform domain models into form-ready values
 * - Centralize option generation logic
 * - Ensure UI consistency and readability
 *
 * Author: Samano CASTRE
 */
export class MandateFormViewModel {

    /** Select options for buyer (contact) field */
    private _ownerOptions = [] as AppEnum[];

    /** Select options for agent field */
    private _userOptions = [] as AppEnum[];

    /** Select options for property field */
    private _propertyOptions = [] as AppEnum[];

    /** Select options for need field */
    private _needOptions = [] as AppEnum[];

    /** Initial values consumed by the form layer (Formik) */
    private _formValues!: MandateFormValues;

    /** Computed title displayed in the form header */
    private _title!: string;

    /** Enums and domain data required to build form values and options */
    private _viewEnums!: MandateEnums;
    private _contactMap!: RecordMap<ContactModel>;
    private _userMap!: RecordMap<UserModel>;
    private _propertyMap!: RecordMap<PropertyModel>;
    private _needMap!: RecordMap<NeedModel>;
    private _enrichedMandate?: MandateModel;

    /** Select options for property field */
    private _propertyValidOptions = [] as AppEnum[];

    /** Select options for need field */
    private _needValidOptions = [] as AppEnum[];

    private _mandateTypeValidOptions = [] as AppEnum[];

    private _property? : PropertyModel;
    private _need? : NeedModel;


    /**
     * Constructor
     *
     * Initializes the ViewModel using a params object. This approach:
     * - Reduces parameter list length
     * - Improves readability
     * - Supports optional enriched mandate for edit mode
     *
     * @param params Object containing enums, domain model lists, and optional enriched mandate
     */
    public constructor(private params: MandateFormVMParams) {
        this.initparams();
        this.initTitle();
        this.initOptions();
        this.initFormValues();
    }

    /**
     * Initializes internal private fields from the params object.
     * Keeps constructor clean and separates parameter assignment from logic.
     */
    private initparams() {
        this._viewEnums = this.params.viewEnums;
        this._contactMap = this.params.contactMap;
        this._userMap = this.params.userMap;
        this._propertyMap = this.params.propertyMap;
        this._needMap = this.params.needMap;
        this._enrichedMandate = this.params.enrichedMandate;
        this._need = this.params.need;
        this._property = this.params.property;
    }
    /**
    * Initializes the form title.
    *
    * - In edit mode: builds a contextual title using mandate id and operation type
    * - In creation mode: falls back to a default title
    */
    private initTitle() {
        this._title = this._enrichedMandate
            ? (
                "Mandat N° "
                + this.id
                + " - "
                + AppEnumHelper.getLabel(
                    this._viewEnums.OperationTypeEnum, 
                    this._enrichedMandate.operationType
                ).toUpperCase()
            )
            : "Création de mandat";
    }

    /**
    * Initializes select options used by the form.
    *
    * Converts domain models into UI-friendly enum-like structures
    * suitable for Select components.
    */
    private initOptions() {
        this._ownerOptions = OptionHelper.ownerOptions(this._contactMap.toArray());
        this._userOptions = OptionHelper.userOptions(this._userMap.toArray());
        this._propertyOptions = OptionHelper.propertyOptions(
            this._propertyMap.toArray(),
            this._viewEnums.PropertyTypeEnum
        );
        this._needOptions = OptionHelper.needOptions(
            this._needMap.toArray(),
            this._viewEnums.PropertyTypeEnum,
            this._viewEnums.NeedStatusEnum
        );

        this.handleOperationTypeChange(this._enrichedMandate?.operationType);
        this.handleMandateTypeChange(this._enrichedMandate?.mandateType);

    }

    /**
     * Initializes form values.
     *
     * Maps the enriched mandate (when present) to form-compatible values,
     * applying formatting and fallback defaults when necessary.
     */
    private initFormValues() {

        this._formValues = {

            agentId: this._enrichedMandate?.agentId ?? "",

            operationType: this._property?.operationType ?? this._need?.operationType ?? this._enrichedMandate?.operationType ?? "",

            startAt: DateFormatHelper.toInput(this._enrichedMandate?.startAt),

            endAt: DateFormatHelper.toInput(this._enrichedMandate?.endAt),

            referrer: this._enrichedMandate?.referrer ?? "",

            notes: this._enrichedMandate?.notes ?? "",

            feeRate: this._enrichedMandate?.feeRate ?? "",

            applicableVat: this._enrichedMandate?.applicableVat ?? "",

            contacts: this._enrichedMandate?.mandateContactLinks?.map(mcLink => String(mcLink.contactId)) ?? [],

            needId : this._need?.id ?? this._enrichedMandate?.needId ?? "",
            propertyId : this._property?.id ?? this._enrichedMandate?.propertyId ?? "",
           
            mandateType: this._enrichedMandate?.mandateType ?? "",


            agencyComTtc: SoNumber.formatAmount(SoVAT.toTTC(this._enrichedMandate?.agencyComHt, this._enrichedMandate?.applicableVat)),

            totalPriceTtc: SoNumber.formatAmount(SoVAT.toTTC(this._enrichedMandate?.totalPriceHt, this._enrichedMandate?.applicableVat)),

        } as MandateFormValues
    }

    private getPropertyPriceById = (propertyId: number) => {
        return this._propertyMap.find(property => property.id === Number(propertyId))?.price ?? 0;
    }

    handleComputeAgencyComTtc(payload: {
        propertyId?: string | number,
        feeRate?: number | null,
        applicableVat?: number | null
    }) {

        const {propertyId} = payload;

        const totalPrice =  propertyId ? this.getPropertyPriceById(propertyId as number) : 0;

        const feeRate = payload.feeRate ?? 0;
        const applicableVat = payload.applicableVat ?? 0;

        const commissionHT = totalPrice * (feeRate / 100);

        return {
            propertyId,
            feeRate,
            applicableVat,
            agencyComTtc: SoNumber.formatAmount(SoVAT.toTTC(commissionHT, applicableVat)),
            totalPriceTtc: SoNumber.formatAmount(SoVAT.toTTC(totalPrice, applicableVat))
        };
    }


    public isPropertyTarget() {
        return 'property' === this._enrichedMandate?.mandateType;
    }

    public isNeedTarget() {
        return 'need' === this._enrichedMandate?.mandateType;
    }

    public isUpdateMode() {
        return !!this.id;
    }


    public handleOperationTypeChange(value?: string | null) {

        if (!value) {
            this.handleMandateTypeChange();
            return;
        }
        const filterForSale = (type: AppEnum) => {
            if (value === OTHER_TYPE) {
                return true;
            }
            return (value === SALE_OPERATION_TYPE)
                ? String(type.value).includes(SALE_OPERATION_TYPE)
                : !String(type.value).includes(SALE_OPERATION_TYPE);
        }

        const filterOptions = this._viewEnums.MandateTypeEnum.filter(filterForSale);
        this._mandateTypeValidOptions = OptionHelper.sortByLabel(filterOptions);

        this.handleMandateTypeChange();
    }


    public handleMandateTypeChange(value?: string) {

        if (!value) {
            this._needValidOptions = [];
            this._propertyValidOptions = [];
        }

        if (value === SEARCH_PROPERTY_RENT_MANADTE_TYPE) {
            this._propertyValidOptions = [];
            this._needValidOptions = this._needOptions.filter(opt => {
                const model = (this._needMap ?? []).find(model => String(model.id) === opt.value);
                return SALE_OPERATION_TYPE !== model?.operationType
            });
        }
        else {
            this._needValidOptions = [];
            const isSaleSelected = value === SALE_MANDATE_TYPE;
            const isRentSelected = value === RENT_MNGT_MANDATE_TYPE;

            if (isSaleSelected || isRentSelected) {
                this._propertyValidOptions = this._propertyOptions.filter(opt => {

                    const model = (this._propertyMap ?? []).find(model => String(model.id) === opt.value);
                    const isModalSalable = SALE_OPERATION_TYPE === model?.operationType;

                    return isSaleSelected
                        ? isModalSalable
                        : !isModalSalable;
                });
            }
        }
    }

    /**
     * Returns initial values consumed by the form layer.
     */
    get formValues() {
        return this._formValues;
    }

    /**
     * Returns the mandate identifier when in edit mode.
     */
    get id() {
        return this._enrichedMandate?.id;
    }

    /**
     * Returns available operation types.
     */
    get operationOptions(): AppEnum[] {
        return this._viewEnums.OperationTypeEnum;
    }

    /**
     * Returns selectable buyer options.
     */
    get ownerOptions(): AppEnum[] {
        return this._ownerOptions;
    }

    /**
     * Returns selectable agent options.
     */
    get userOptions(): AppEnum[] {
        return this._userOptions;
    }

    /**
     * Returns selectable property options.
     */
    get propertyValidOptions(): AppEnum[] {
        return this._propertyValidOptions;
    }

    /**
    * Returns selectable need options.
    */
    get needValidOptions(): AppEnum[] {
        return this._needValidOptions;
    }


    get mandateTypeOptions(): AppEnum[] {
        return this._mandateTypeValidOptions;
    }


    hasValidPropertyOptions() {
        return !!this.propertyValidOptions.length
    }

    hasValidNeedOptions() {
        return !!this.needValidOptions.length
    }

    /**
     * Returns the computed form title.
     */
    get title() {
        return this._title;
    }
}